import { apiService } from './apiService.js';
import { isLeadContactBlocked } from '../config/contactBlocklist.js';
import { justiceSphereLabel } from '../utils/justiceSphere.js';

/**
 * EnrichedLeadService - Serviço para o módulo reformulado de leads
 * Foca em leads de processos com dados enriquecidos
 */
class EnrichedLeadService {

  buildEnrichedLeadsParams(filters = {}, extra = {}) {
    const params = new URLSearchParams(extra);

    if (filters.justica && filters.justica.length > 0) {
      filters.justica.forEach((j) => params.append('justica', j));
    }

    if (filters.tribunal && filters.tribunal.length > 0) {
      filters.tribunal.forEach((t) => params.append('tribunal', t));
    }

    if (filters.estado && filters.estado.length > 0) {
      filters.estado.forEach((e) => params.append('estado', e));
    }

    if (filters.provincia && filters.provincia.length > 0) {
      filters.provincia.forEach((p) => params.append('provincia', p));
    }

    if (filters.cidade && filters.cidade.length > 0) {
      filters.cidade.forEach((c) => params.append('cidade', c));
    }

    if (filters.poloPassivo) {
      params.append('poloPassivo', filters.poloPassivo);
    }

    if (filters.advogado) {
      params.append('advogado', filters.advogado);
    }

    if (filters.valorEstimadoMin !== undefined) {
      params.append('valorEstimadoMin', filters.valorEstimadoMin.toString());
    }

    if (filters.valorEstimadoMax !== undefined) {
      params.append('valorEstimadoMax', filters.valorEstimadoMax.toString());
    }

    if (filters.scoreProcessoMin !== undefined) {
      params.append('scoreProcessoMin', filters.scoreProcessoMin.toString());
    }

    if (filters.scoreProcessoMax !== undefined) {
      params.append('scoreProcessoMax', filters.scoreProcessoMax.toString());
    }

    if (filters.search) {
      params.append('search', filters.search);
    }

    if (filters.status && filters.status.length > 0) {
      filters.status.forEach((s) => params.append('status', s));
    }

    if (filters.excludeStatus && filters.excludeStatus.length > 0) {
      filters.excludeStatus.forEach((s) => params.append('excludeStatus', s));
    }

    if (filters.convertedAtStart) {
      params.append('convertedAtStart', filters.convertedAtStart);
    }

    if (filters.convertedAtEnd) {
      params.append('convertedAtEnd', filters.convertedAtEnd);
    }

    if (filters.lostAtStart) {
      params.append('lostAtStart', filters.lostAtStart);
    }

    if (filters.lostAtEnd) {
      params.append('lostAtEnd', filters.lostAtEnd);
    }

    return params;
  }

  /**
   * Buscar leads enriquecidos com filtros de trader
   */
  async getEnrichedLeads(filters = {}, page = 1, limit = 20) {
    try {
      const params = this.buildEnrichedLeadsParams(filters, {
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await apiService.get(`/leads/enriched?${params}`);
      return {
        leads: response.data,
        pagination: response.pagination,
        total: response.pagination?.total || 0
      };
    } catch (error) {
      console.error('Erro ao buscar leads enriquecidos:', error);
      throw error;
    }
  }

  /**
   * Exportar leads enriquecidos em planilha Excel (mesmos filtros da listagem)
   */
  async exportEnrichedLeadsExcel(filters = {}) {
    const params = this.buildEnrichedLeadsParams(filters);
    const query = params.toString();
    const endpoint = query
      ? `/leads/enriched/export?${query}`
      : '/leads/enriched/export';

    const { blob, filename } = await apiService.download(endpoint);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = filename || 'leads_ecp.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return { filename: filename || 'leads_ecp.xlsx' };
  }

  /**
   * Buscar lead enriquecido por ID com dados completos
   */
  async getEnrichedLeadById(id) {
    try {
      const response = await apiService.get(`/leads/enriched/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar lead enriquecido:', error);
      throw error;
    }
  }

  /**
   * Buscar opções de filtros disponíveis
   */
  async getFilterOptions() {
    try {
      const response = await apiService.get('/leads/filters/options');
      return response.data || { tribunais: [], estados: [], esferas: [] };
    } catch (error) {
      console.error('Erro ao buscar opções de filtros:', error);
      return { tribunais: [], estados: [], esferas: [] };
    }
  }

  /**
   * Buscar métricas para traders
   */
  async getTraderMetrics() {
    try {
      const response = await apiService.get('/leads/metrics/trader');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar métricas de traders:', error);
      throw error;
    }
  }

  /**
   * Gerar link do WhatsApp para um telefone
   * @param {Object} phone - Objeto phone com ddd e telefone
   * @param {string} leadName - Nome do lead para personalizar mensagem
   * @returns {string} Link formatado do WhatsApp Web
   */
  generateWhatsAppLink(phone, leadName) {
    const cleanPhone = `${phone.ddd}${phone.telefone}`.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Olá ${leadName}, sou da Wone Ativos Judiciais e gostaria de falar sobre seu processo judicial.`
    );
    return `https://wa.me/55${cleanPhone}?text=${message}`;
  }

  /**
   * Metadados de exibição do status do funil (Em contato / Convertido / Perdida / Inapto)
   * Retorna null para status "Novo"/sem contato (nenhum badge).
   */
  getStatusMeta(status) {
    switch (status) {
      case 'CONVERTED':
        return {
          key: 'CONVERTED',
          label: 'Convertido',
          className: 'bg-green-100 text-green-700 border-green-200',
        };
      case 'LOST':
        return {
          key: 'LOST',
          label: 'Perdida',
          className: 'bg-red-100 text-red-700 border-red-200',
        };
      case 'REJECTED':
        return {
          key: 'REJECTED',
          label: 'Inapto',
          className: 'bg-slate-100 text-slate-700 border-slate-300',
        };
      case 'CONTACTED':
      case 'PROPOSAL_SENT':
      case 'NEGOTIATING':
        return {
          key: 'CONTACTED',
          label: 'Em contato',
          className: 'bg-blue-100 text-blue-700 border-blue-200',
        };
      default:
        // NEW / QUALIFIED (legado): sem badge — ainda não houve contato humano
        return null;
    }
  }

  /** Contato bloqueado: flag no lead ou parceiro RUIM (passado via matchedPartner) */
  isContactBlocked(contactOrLead, matchedPartner = null) {
    return isLeadContactBlocked(contactOrLead, matchedPartner);
  }

  /** Indica se o contato já está em atendimento (bloqueia novo contato) */
  /**
   * Contato bloqueado para novo início de atendimento.
   * CONTACTED só trava enquanto houver responsável (atendimento em andamento).
   * Após Encerrar, assignedTo é limpo e o lead pode ser contactado de novo.
   */
  isContactBusy(status, assignedTo) {
    if (['PROPOSAL_SENT', 'NEGOTIATING', 'CONVERTED', 'REJECTED'].includes(status)) {
      return true;
    }
    if (status === 'CONTACTED') {
      return Boolean(assignedTo);
    }
    return false;
  }

  /** Nome do usuário autenticado para registrar quem está em contato */
  resolveContactUserName(user) {
    return user?.name || user?.username || user?.email || undefined;
  }

  /** Resolve os leadIds de um card consolidado (ou lead avulso) */
  resolveCardLeadIds(lead) {
    if (Array.isArray(lead?.leadIds) && lead.leadIds.length > 0) {
      return lead.leadIds.filter(Boolean);
    }
    // Em card consolidado, lead.id é processId — nunca usar como id de Lead.
    if (lead?.primaryLeadId) return [lead.primaryLeadId];
    if (Array.isArray(lead?.contacts) && lead.contacts.length > 0) {
      return lead.contacts.map((c) => c.leadId).filter(Boolean);
    }
    // Lead avulso (não consolidado): id é o próprio lead
    if (lead?.id && lead.id !== lead?.processId) return [lead.id];
    return [];
  }

  /** Marca um contato (lead) como "Em contato" pelo usuário informado */
  async markInContact(leadId, assignedTo) {
    const response = await apiService.put(`/leads/${leadId}`, {
      status: 'CONTACTED',
      assignedTo: assignedTo || undefined,
      lastContactAt: new Date().toISOString(),
    });
    return response.data;
  }

  /** Ativa/desativa bloqueio operacional de contato */
  async setContactBlocked(leadId, contactBlocked) {
    const response = await apiService.put(`/leads/${leadId}`, {
      contactBlocked: Boolean(contactBlocked),
    });
    return response.data;
  }

  /** Marca um contato (lead) como "Convertido" */
  async markConverted(leadId) {
    const response = await apiService.put(`/leads/${leadId}`, {
      status: 'CONVERTED',
      convertedAt: new Date().toISOString(),
    });
    return response.data;
  }

  /** Marca um contato (lead) como "Perdida" com motivo */
  async markLost(leadId, reason) {
    const response = await apiService.put(`/leads/${leadId}`, {
      status: 'LOST',
      lostAt: new Date().toISOString(),
      lostReason: reason || undefined,
    });
    return response.data;
  }

  /** Marca um contato (lead) como "Inapto" com motivo opcional */
  async markRejected(leadId, reason) {
    const response = await apiService.put(`/leads/${leadId}`, {
      status: 'REJECTED',
      lostAt: new Date().toISOString(),
      lostReason: reason || undefined,
    });
    return response.data;
  }

  /** Aplica status terminal a todos os leads de um card */
  async applyStatusToCard(lead, action, reason) {
    const leadIds = this.resolveCardLeadIds(lead);
    if (leadIds.length === 0) {
      throw new Error('Lead sem identificador para atualizar status');
    }
    await Promise.all(
      leadIds.map((id) => {
        if (action === 'CONVERTED') return this.markConverted(id);
        if (action === 'LOST') return this.markLost(id, reason);
        if (action === 'REJECTED') return this.markRejected(id, reason);
        return Promise.resolve();
      }),
    );
  }

  /** Reabre um contato (volta para "Novo") */
  async reopenContact(leadId) {
    const response = await apiService.put(`/leads/${leadId}`, {
      status: 'NEW',
      assignedTo: null,
      lastContactAt: null,
      lostReason: null,
    });
    return response.data;
  }

  /**
   * Mapear tipo de justiça para exibição
   */
  getJusticaLabel(tipo) {
    return justiceSphereLabel(tipo, 'filterLabel') || tipo;
  }

  /**
   * Obter cor para badge de score
   */
  getScoreColor(score) {
    if (score >= 80) return 'bg-green-500 text-white';
    if (score >= 65) return 'bg-blue-500 text-white';
    if (score >= 50) return 'bg-yellow-500 text-white';
    if (score >= 30) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  }

  /**
   * Obter ícone para tipo de justiça
   */
  getJusticaIcon(tipo) {
    const icons = {
      'comum': '⚖️',
      'federal': '🏛️',
      'trabalho': '👷'
    };
    return icons[tipo] || '⚖️';
  }

  /**
   * Formatar valor monetário
   */
  formatCurrency(value) {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formatar número de processo CNJ
   */
  formatProcessNumber(numero) {
    if (!numero) return 'N/A';
    // Se já estiver formatado, retornar
    if (numero.includes('-')) return numero;
    
    // Formato CNJ: NNNNNNN-DD.AAAA.J.TR.OOOO
    if (numero.length === 20) {
      return `${numero.substring(0, 7)}-${numero.substring(7, 9)}.${numero.substring(9, 13)}.${numero.substring(13, 14)}.${numero.substring(14, 16)}.${numero.substring(16, 20)}`;
    }
    
    return numero;
  }
}

export const enrichedLeadService = new EnrichedLeadService();